/**
 * Hotel and room filtering for the sidebar's hotel / room filters.
 *
 * Every filter reads a field the hotel-cards playbook actually returns:
 *
 *   hotel  stars, rating (out of 10), pricePerNight, breakfastIncluded,
 *          amenities[], rooms[]
 *   room   type (the room name), pricePerNight, breakfastIncluded, refundable
 *
 * Filters that the data cannot answer (pool, wifi, property type) are not
 * offered, because they would hide every result.
 */
import type { HotelFilters, RoomFilters } from '../types/filterSidebar';

/** Loose shapes: Chat keeps hotels and rooms as `any`. */
type HotelLike = {
  stars?: number;
  rating?: number;
  pricePerNight?: number;
  breakfastIncluded?: boolean;
  amenities?: unknown[];
  rooms?: RoomLike[];
};

type RoomLike = {
  type?: string;
  pricePerNight?: number;
  breakfastIncluded?: boolean;
  refundable?: boolean;
};

/** Amenity options the sidebar offers — the ones the data can answer. */
export const HOTEL_AMENITY_OPTIONS = ['Breakfast', 'Free cancellation'];
export const ROOM_AMENITY_OPTIONS = ['Breakfast', 'Free cancellation'];
export const ROOM_BED_TYPE_OPTIONS = ['Any', 'Single', 'Double', 'Twin', 'Queen', 'King'];

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const hotelHasAmenity = (hotel: HotelLike, amenity: string): boolean => {
  if (amenity === 'Breakfast') {
    return (
      Boolean(hotel.breakfastIncluded) ||
      Boolean(hotel.rooms?.some((room) => room.breakfastIncluded)) ||
      Boolean(hotel.amenities?.some((a) => String(a).toLowerCase() === 'breakfast'))
    );
  }
  if (amenity === 'Free cancellation') {
    return Boolean(hotel.rooms?.some((room) => room.refundable));
  }
  const wanted = amenity.toLowerCase();
  return Boolean(hotel.amenities?.some((a) => String(a).toLowerCase() === wanted));
};

/** Every selected amenity has to be present; an empty selection is "any". */
export const filterHotels = <T extends HotelLike>(hotels: T[], filters?: HotelFilters): T[] => {
  if (!filters) return hotels;
  return hotels.filter((hotel) => {
    if (filters.stars.length && !filters.stars.includes(num(hotel.stars))) return false;
    // A price or rating of 0 means the provider didn't send one, so it
    // can't satisfy a limit.
    if (filters.maxPrice) {
      const price = num(hotel.pricePerNight);
      if (price <= 0 || price > filters.maxPrice) return false;
    }
    if (filters.guestRating > 0 && num(hotel.rating) < filters.guestRating) return false;
    return filters.amenities.every((amenity) => hotelHasAmenity(hotel, amenity));
  });
};

export const filterRooms = <T extends RoomLike>(rooms: T[], filters?: RoomFilters): T[] => {
  if (!filters) return rooms;
  return rooms.filter((room) => {
    if (filters.bedType !== 'Any' && !String(room.type || '').toLowerCase().includes(filters.bedType.toLowerCase())) {
      return false;
    }
    if (filters.maxPrice) {
      const price = num(room.pricePerNight);
      if (price <= 0 || price > filters.maxPrice) return false;
    }
    return filters.amenities.every((amenity) => {
      if (amenity === 'Breakfast') return Boolean(room.breakfastIncluded);
      if (amenity === 'Free cancellation') return Boolean(room.refundable);
      return false;
    });
  });
};

/** Highest nightly rate in the results, rounded up to the slider step. */
export const maxNightlyPrice = (items: { pricePerNight?: number }[], step: number, fallback: number): number => {
  const top = Math.max(0, ...items.map((item) => num(item.pricePerNight)));
  return top > 0 ? Math.ceil(top / step) * step : fallback;
};
